import { createSlice } from "@reduxjs/toolkit";

const THEME_DARK = "dark";
const THEME_LIGHT = "light";

const LS_THEME = "theme";
const LS_SECTIONS = "sidebarCollapsedSections";

/**
 * Тёмная тема — только по явному выбору. Раньше сюда попадало всё, что не
 * равно "light", включая null у нового пользователя, и первый вход оказывался
 * тёмным вопреки светлому дефолту в index.css и анти-FOUC-скрипте.
 */
function normalizeTheme(theme) {
  return theme === THEME_DARK ? THEME_DARK : THEME_LIGHT;
}

/** localStorage может бросить (приватный режим, отключённые cookies) — глотаем. */
function lsGet(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function lsSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    /* не критично: это кэш, а не источник истины */
  }
}

/**
 * Тема из localStorage — только для мгновенного старта, чтобы не было белой
 * вспышки при перезагрузке (парная логика — скрипт в public/index.html).
 * Источник истины остаётся сервер: AuthenticatedLayout перезапишет значение
 * из auth.user.theme, как только придёт /me.
 */
function initialTheme() {
  return normalizeTheme(lsGet(LS_THEME));
}

/** Свёрнутые секции сайдбара — сугубо браузерная настройка, на сервер не едет. */
function initialCollapsedSections() {
  try {
    const raw = lsGet(LS_SECTIONS);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === "string") : [];
  } catch (e) {
    return [];
  }
}

const uiSlice = createSlice({
  name: "ui",
  initialState: {
    sidebarCollapsed: false,
    theme: initialTheme(),
    collapsedSections: initialCollapsedSections(),
  },
  reducers: {
    toggleSidebar(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setSidebarCollapsed(state, action) {
      state.sidebarCollapsed = Boolean(action.payload);
    },
    setTheme(state, action) {
      const next = normalizeTheme(action.payload);
      state.theme = next;
      lsSet(LS_THEME, next);
    },
    toggleSection(state, action) {
      const key = action.payload;
      if (!key) return;
      const idx = state.collapsedSections.indexOf(key);
      if (idx === -1) state.collapsedSections.push(key);
      else state.collapsedSections.splice(idx, 1);
      lsSet(LS_SECTIONS, JSON.stringify(state.collapsedSections));
    },
  },
});

export const { toggleSidebar, setSidebarCollapsed, setTheme, toggleSection } =
  uiSlice.actions;

export const selectSidebarCollapsed = (state) => Boolean(state.ui?.sidebarCollapsed);
export const selectTheme = (state) => normalizeTheme(state.ui?.theme);
export const selectCollapsedSections = (state) => state.ui?.collapsedSections || [];
export const THEMES = Object.freeze({ DARK: THEME_DARK, LIGHT: THEME_LIGHT });

export default uiSlice.reducer;
